import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as createRequestId } from "uuid";
import type { CoreConversationWithMessagesModel, RuntimeChatCapabilitiesModel } from "../../api/api";
import type { CoreChatMessageRequest, CoreChatOpenRequest, ReactChatClient } from "../../api/ReactChatClient";
import type { ReactComponentContext } from "../Contexts/ReactRuntimeBridge";
import { DisplayChatMessage, mergeMessages } from "./chatSessionMessages";

import { chatErrorMessage, chatSendWasRejected, committedChatMessageId } from "./chatErrors";
import { chatActivityStartedAt } from "./chatPolling";
import { useAgentTyping } from "./useAgentTyping";
import { useChatPolling } from "./useChatPolling";

const maximumUpdatePages = 10;

/** Managed sessions start only from explicit actions; polling is serialized, bounded and abortable. */
export type ChatSession = CoreConversationWithMessagesModel & { capabilities?: RuntimeChatCapabilitiesModel; hasMoreUpdates?: boolean };
export type ChatSessionApi = Pick<ReturnType<ReactChatClient["forHost"]>, "openSession" | "getSession" | "getUpdates" | "getMessages" | "sendMessage">;

export function useChatSession(ctx: Pick<ReactComponentContext, "core">, options: { agentId: string; sessionKey?: string }) {
  return useChatSessionEngine(ctx.core.chat, options);
}

export function useChatSessionEngine(chat: ChatSessionApi, options: { agentId?: string; sessionKey?: string }, initial?: ChatSession) {
  const { agentId, sessionKey } = options;
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const initialId = initial?.id;
  const [session, setSession] = useState<ChatSession | undefined>(initial);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [awaitingSince, setAwaitingSince] = useState<number>();
  const [optimistic, setOptimistic] = useState<DisplayChatMessage>();
  const displayIds = useRef(new Map<string, string>());
  const acknowledgedSend = useRef<string>();
  const isAgentTyping = useAgentTyping(session, awaitingSince);
  const current = useRef(session);
  const lifetime = useRef(new AbortController());
  const opening = useRef<Promise<CoreConversationWithMessagesModel>>();
  const refreshing = useRef<Promise<void>>();
  const older = useRef(false);
  const sendInFlight = useRef(false);
  const sentAt = useRef(0);
  const pendingSend = useRef<{ fingerprint: string; requestId: string }>();
  const pendingOpen = useRef<string>();

  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    current.current = initialRef.current;
    opening.current = undefined;
    refreshing.current = undefined;
    older.current = false;
    sendInFlight.current = false;
    sentAt.current = 0;
    pendingSend.current = undefined;
    displayIds.current.clear();
    acknowledgedSend.current = undefined;
    setOptimistic(undefined);
    pendingOpen.current = undefined;
    setSession(initialRef.current);
    setAwaitingSince(undefined);
    setError(undefined);
    setBusy(false);
    setSending(false);
    setLoadingOlder(false);
    return () => {
      controller.abort();
      // Session switches replace the controller within this effect's lifetime.
      lifetime.current.abort();
    };
  }, [chat, agentId, sessionKey, initialId]);

  const update = useCallback((value: ChatSession) => {
    current.current = value;
    setSession((previous) =>
      previous &&
      previous.messages === value.messages &&
      JSON.stringify({ ...previous, messages: undefined }) === JSON.stringify({ ...value, messages: undefined })
        ? previous
        : value
    );
  }, []);

  const refresh = useCallback((): Promise<void> => {
    if (refreshing.current) return refreshing.current;
    const selected = current.current;
    if (!selected) return Promise.resolve();
    const signal = lifetime.current.signal;
    const work = async () => {
      let latest: ChatSession;
      let incoming;
      const incremental = selected.lastMessageSequence != null && Boolean(chat.getUpdates);
      if (incremental) {
        let cursor = selected.lastMessageSequence!;
        incoming = [] as NonNullable<ChatSession["messages"]>;
        let pages = 0;
        do {
          latest = await chat.getUpdates(selected.id, cursor, { sessionKey, signal });
          incoming.push(...(latest.messages ?? []));
          const nextCursor = latest.lastMessageSequence ?? cursor;
          if (nextCursor <= cursor) break;
          cursor = nextCursor;
          pages++;
        } while (latest.hasMoreUpdates && pages < maximumUpdatePages && !signal.aborted);
      } else {
        latest = await chat.getSession(selected.id, { sessionKey, signal });
        incoming = latest.messages ?? [];
      }
      const known = new Set(selected.messages?.map((message) => message.id));
      let cursor = latest.oldestMessageSequence;
      let more = latest.hasMoreMessages;
      // Recover replies spanning more than the newest 50 messages without dropping earlier history.
      while (!incremental && known.size && more && cursor != null && !incoming.some((message) => known.has(message.id))) {
        const page = await chat.getMessages(selected.id, cursor, { sessionKey, signal });
        incoming = [...page.messages, ...incoming];
        if (cursor === page.oldestMessageSequence) break;
        cursor = page.oldestMessageSequence;
        more = page.hasMoreMessages;
      }
      if (!signal.aborted && current.current?.id === selected.id) {
        const previous = current.current;
        update({
          ...latest,
          messages: mergeMessages(previous.messages ?? [], incoming),
          hasMoreMessages: incremental || previous.messages?.length ? previous.hasMoreMessages : latest.hasMoreMessages,
          oldestMessageSequence: incremental || previous.messages?.length ? previous.oldestMessageSequence : latest.oldestMessageSequence,
        });
        setError(undefined);
      }
    };
    const promise = work()
      .catch((reason) => {
        if (!signal.aborted) setError(chatErrorMessage(reason, "Could not refresh chat."));
        throw reason;
      })
      .finally(() => {
        if (refreshing.current === promise) refreshing.current = undefined;
      });
    refreshing.current = promise;
    return promise;
  }, [chat, sessionKey, update]);

  const polling = useChatPolling(
    session?.id,
    refresh,
    () => (current.current?.hasMoreUpdates ? Date.now() : chatActivityStartedAt(current.current, sentAt.current)),
    initialId !== undefined
  );
  const notifyActivity = polling.notifyActivity;

  const open = useCallback(
    (request: Omit<CoreChatOpenRequest, "agentId"> = {}): Promise<CoreConversationWithMessagesModel> => {
      if (opening.current) return opening.current;
      const signal = lifetime.current.signal;
      setBusy(true);
      const requestId = request.forceNew ? (request.requestId ?? (pendingOpen.current ??= createRequestId())) : request.requestId;
      const promise = chat
        .openSession({ ...request, ...(requestId ? { requestId } : {}), agentId: agentId ?? "" }, { sessionKey, signal })
        .then((value) => {
          if (signal.aborted) throw new DOMException("Opening chat was cancelled.", "AbortError");
          if (!signal.aborted) {
            pendingOpen.current = undefined;
            // A session switch invalidates every read/upload/send owned by the previous session.
            if (current.current && current.current.id !== value.id) {
              lifetime.current.abort();
              lifetime.current = new AbortController();
              refreshing.current = undefined;
              sendInFlight.current = false;
              older.current = false;
              pendingSend.current = undefined;
              displayIds.current.clear();
              acknowledgedSend.current = undefined;
              setOptimistic(undefined);
              setSending(false);
              setBusy(false);
              setLoadingOlder(false);
            }
            update(value);
            setAwaitingSince(undefined);
            setError(undefined);
            sentAt.current = 0;
          }
          return value;
        })
        .catch((reason) => {
          if (!signal.aborted) setError(chatErrorMessage(reason, "Could not open chat."));
          throw reason;
        })
        .finally(() => {
          if (opening.current === promise) opening.current = undefined;
          if (!signal.aborted) setBusy(false);
        });
      opening.current = promise;
      return promise;
    },
    [chat, agentId, sessionKey, update]
  );

  const send = useCallback(
    async (request: CoreChatMessageRequest): Promise<string> => {
      if (!current.current) throw new Error("Open a chat session before sending.");
      if (sendInFlight.current) throw new Error("A message is already being sent.");
      const signal = lifetime.current.signal;
      const fingerprint = JSON.stringify([current.current.id, request.message, request.fileIds ?? []]);
      if (pendingSend.current && pendingSend.current.fingerprint !== fingerprint) {
        throw new Error("The previous send was not confirmed. Retry the original message before sending a different one.");
      }
      const requestId = request.requestId ?? (pendingSend.current ??= { fingerprint, requestId: createRequestId() }).requestId;
      sendInFlight.current = true;
      setSending(true);
      sentAt.current = Date.now();
      const localId = `pending-${requestId}`;
      acknowledgedSend.current = undefined;
      setOptimistic({
        id: localId,
        clientMessageId: localId,
        pendingSend: true,
        participantId: current.current.participants?.find((participant) => participant.type === "User")?.id ?? "",
        createdDate: new Date(sentAt.current).toISOString(),
        contents: [
          ...(request.message ? [{ id: `${localId}-text`, text: request.message }] : []),
          ...(request.fileIds ?? []).map((fileId) => ({ id: `${localId}-${fileId}`, fileId })),
        ],
      });
      setAwaitingSince(sentAt.current);
      const acknowledge = (id: string) => {
        acknowledgedSend.current = id;
        displayIds.current.set(id, localId);
        setOptimistic((message) => (message ? { ...message, pendingSend: false } : undefined));
      };
      try {
        const id = await chat.sendMessage(current.current.id, { ...request, requestId }, { sessionKey, signal });
        if (!signal.aborted) {
          acknowledge(id);
          pendingSend.current = undefined;
          notifyActivity();
          // Send succeeded: a failed refresh must not encourage a duplicate send.
          try {
            await refresh();
          } catch {
            /* refresh exposes its own error */
          }
        }
        return id;
      } catch (reason) {
        const committed = committedChatMessageId(reason);
        if (committed && !signal.aborted) {
          acknowledge(committed);
          pendingSend.current = undefined;
          notifyActivity();
          try {
            await refresh();
          } catch {
            /* The committed send remains successful. */
          }
          if (!signal.aborted) setError(chatErrorMessage(reason, "Message saved; attachments are pending."));
          return committed;
        }
        if (!signal.aborted) {
          if (chatSendWasRejected(reason)) {
            setOptimistic(undefined);
            pendingSend.current = undefined;
            sentAt.current = 0;
          } else notifyActivity();
          setAwaitingSince(undefined);
          setError(chatErrorMessage(reason, "Could not send message."));
        }
        throw reason;
      } finally {
        if (!signal.aborted) {
          sendInFlight.current = false;
          setSending(false);
        }
      }
    },
    [chat, sessionKey, refresh, notifyActivity]
  );

  const loadOlder = useCallback(async () => {
    const selected = current.current;
    if (!selected?.hasMoreMessages || selected.oldestMessageSequence == null || older.current) return false;
    older.current = true;
    setLoadingOlder(true);
    const signal = lifetime.current.signal;
    try {
      const page = await chat.getMessages(selected.id, selected.oldestMessageSequence, { sessionKey, signal });
      if (!signal.aborted && current.current?.id === selected.id) {
        update({
          ...current.current,
          messages: mergeMessages(current.current.messages ?? [], page.messages, true),
          hasMoreMessages: page.hasMoreMessages,
          oldestMessageSequence: page.oldestMessageSequence,
        });
        setError(undefined);
        return true;
      }
      return false;
    } catch (reason) {
      if (!signal.aborted) setError(chatErrorMessage(reason, "Could not load history."));
      throw reason;
    } finally {
      if (!signal.aborted) {
        older.current = false;
        setLoadingOlder(false);
      }
    }
  }, [chat, sessionKey, update]);

  const resumePolling = polling.resume;
  const manualRefresh = useCallback(async () => {
    await refresh();
    resumePolling();
  }, [refresh, resumePolling]);

  // Keep optimistic UI out of persisted-message cursors, paging and polling state.
  const displayedSession = useMemo(() => {
    if (!session || (!optimistic && !displayIds.current.size)) return session;
    const messages = (session.messages ?? []).map((message) => {
      const clientMessageId = displayIds.current.get(message.id);
      return clientMessageId ? { ...message, clientMessageId } : message;
    });
    if (optimistic && !messages.some((message) => message.id === acknowledgedSend.current)) messages.push(optimistic);
    return { ...session, messages };
  }, [session, optimistic]);
  return { session: displayedSession, error, busy, sending, isAgentTyping, awaitingSince, loadingOlder, open, send, refresh: manualRefresh, loadOlder };
}
